/** @jsxImportSource @emotion/react */
import React from 'react';
import type { PlanCell, PlanCellKind, StructuralObject } from '../../../types/plan';
import { planCellKey } from '../../../utils/planCells';
import { isBedEntity, isPlotEntity } from '../../../utils/planEntities';
import { styles, contourHover, contourSelected } from '../PlanGrid.styles';
import type { EntityInteractionState } from '../../../hooks/useEntityInteraction';
import type { EntityLookup } from '../../../utils/planEntities';
import type { PlotGridLayers } from '../../../types/bulkOperations';
import type { PlanAppMode, PlanLayerVisibility } from '../../../types/planManagement';

const cellColorByKind: Record<PlanCellKind, string> = {
  path: '#E0E0E0',
  obstacle: '#9E9E9E',
  bed: '#D7CCC8',   // soil / brown
  plot: '#BBDEFB',
  empty: '#FFFFFF',
};

const BED_BORDER_COLOR = '#A1887F'; // brown border for bed perimeter

interface PlanGridRowProps {
  rowGlobal: number;
  cols: number[];
  cellMap: Map<string, PlanCell>;
  structuralObjectByCell: Map<string, StructuralObject>;
  isSelectedCell: (row: number, col: number) => boolean;
  highlightCells?: Set<string>;
  selectionRect: { occupiedCells: string[]; exceedsBoundary?: boolean } | null;
  entityState: EntityInteractionState;
  entityLookup: EntityLookup;
  multiSelectedBeds: Set<string>;
  multiSelectedPlots: Set<string>;
  appMode: PlanAppMode;
  allowBedSelection: boolean;
  isTransplantTargetMode: boolean;
  mode: 'navigate' | 'erase';
  contourBorders: Map<string, { top: boolean; right: boolean; bottom: boolean; left: boolean }>;
  ghContourBorders: Map<string, { top: boolean; right: boolean; bottom: boolean; left: boolean }>;
  ghCellGroupMap: Map<string, string>;
  ghCrossbars: Map<string, 'vertical' | 'horizontal'>;
  activeGreenhouseGroups: Set<string>;
  hoveredGreenhouseGroups: Set<string>;
  planningSelectedCells?: Set<string>;
  bulkPlacementPreview: { previewCells: string[]; isValid: boolean } | null;
  bedsWithPlantings?: Set<string>;
  bedBoundaryBorders: Map<string, { top: boolean; right: boolean; bottom: boolean; left: boolean }>;
  bedPlantingCells: Set<string>;
  bedWateringCells: Set<string>;
  layerData?: PlotGridLayers;
  layerVisibility?: PlanLayerVisibility;
  onCellMouseDown: (row: number, col: number, e: React.MouseEvent<HTMLDivElement>) => void;
  onCellMouseEnter: (row: number, col: number) => void;
  onCellMouseLeave: () => void;
  onCellMouseUp: () => void;
}

const PlanGridRow: React.FC<PlanGridRowProps> = (props) => {
  return (
    <div css={styles.row} data-plan-row={props.rowGlobal}>
      {props.cols.map((colGlobal) => {
        const key = planCellKey(props.rowGlobal, colGlobal);
        const cell = props.cellMap.get(key);
        const isSelected = props.isSelectedCell(props.rowGlobal, colGlobal);
        const isHighlighted = props.highlightCells?.has(key);
        const isOccupied = props.selectionRect?.occupiedCells.includes(key);
        const selectionExceedsBoundary = props.selectionRect?.exceedsBoundary === true;
        const isInBulkPreview = props.bulkPlacementPreview?.previewCells.includes(key);
        const isBulkPreviewValid = props.bulkPlacementPreview?.isValid;
        const isEntityHovered = props.entityState.hoveredCellKeys.has(key);
        const isEntitySelected = props.entityState.selectedCellKeys.has(key);
        const entity = props.entityLookup.get(key);
        const isMultiSelected =
          (entity && isBedEntity(entity) && props.multiSelectedBeds.has(entity.bedId)) ||
          (entity && isPlotEntity(entity) && props.multiSelectedPlots.has(entity.plotId));
        const isBedCell = cell?.kind === 'bed';
        const showPlantingDot = isBedCell && props.bedPlantingCells.has(key);
        const structuralObject = props.structuralObjectByCell.get(key);
        const showCovering = structuralObject?.kind === 'covering';
        const structureVisual = (() => {
          if (!structuralObject || structuralObject.kind === 'covering') return null;
          switch (structuralObject.subtype) {
            case 'outbuilding':
              return { label: 'ХП', color: '#795548', title: 'Хозяйственная постройка' };
            case 'solid_fence':
              return { label: 'З', color: '#5d4037', title: 'Сплошной забор' };
            case 'lamp_post':
              return { label: 'Ф', color: '#d4a017', title: 'Отдельно стоящий фонарь' };
            default:
              return { label: 'О', color: '#607d8b', title: structuralObject.name || structuralObject.subtype };
          }
        })();
        const ghGroupId = props.ghCellGroupMap.get(key);
        const ghBorder = ghGroupId ? props.ghContourBorders.get(key) : null;
        const crossbarOrientation = ghGroupId ? props.ghCrossbars.get(key) : null;
        // Suppress greenhouse tint when: a bed inside is selected, OR cursor is anywhere over this group
        const isGhTintActive = ghGroupId
          && !props.activeGreenhouseGroups.has(ghGroupId)
          && !props.hoveredGreenhouseGroups.has(ghGroupId);
        const bedId = entity && isBedEntity(entity) ? entity.bedId : undefined;
        const wateringInfo = bedId ? props.layerData?.watering?.byBed?.[bedId] : undefined;
        const issueInfo = bedId ? props.layerData?.issues?.byBed?.[bedId] : undefined;
        const healthInfo = bedId ? props.layerData?.health?.byBed?.[bedId] : undefined;
        const showWateringLayer = Boolean(
          props.layerVisibility?.watering
          && wateringInfo
          && props.bedWateringCells.has(key)
          && wateringInfo.indicatorLevel
        );
        const showIssuesLayer = Boolean(props.layerVisibility?.issues && issueInfo && (issueInfo.openIssues > 0 || issueInfo.recentIssues > 0));
        const showDiseaseLayer = Boolean(showPlantingDot && healthInfo?.activeDiseases && healthInfo.activeDiseases.length > 0);
        const wateringColor = (() => {
          if (!wateringInfo) return '#d9d9d9';
          if (wateringInfo.indicatorLevel === 'blue') return '#1890ff';
          if (wateringInfo.indicatorLevel === 'yellow') return '#faad14';
          if (wateringInfo.indicatorLevel === 'red') return '#ff4d4f';
          return '#d9d9d9';
        })();
        const issuesColor = issueInfo?.severity === 'high' ? '#ff4d4f' : '#faad14';
        const isPlanningSelected = props.planningSelectedCells?.has(key);
        const background = (() => {
          if (!cell?.kind) return '#fff';
          if (cell.kind !== 'bed') return cellColorByKind[cell.kind];
          if (!props.layerVisibility?.watering || !wateringInfo) return cellColorByKind.bed;
          if (wateringInfo.status === 'fresh') return '#bf9d7f';
          return cellColorByKind.bed;
        })();

        // Bed boundary borders: suppress internal grid lines, show perimeter
        const bedBounds = isBedCell ? props.bedBoundaryBorders.get(key) : undefined;
        const bedBorderStyle = isBedCell && bedBounds ? {
          borderTopColor: bedBounds.top ? BED_BORDER_COLOR : cellColorByKind.bed,
          borderRightColor: bedBounds.right ? BED_BORDER_COLOR : cellColorByKind.bed,
          borderBottomColor: bedBounds.bottom ? BED_BORDER_COLOR : cellColorByKind.bed,
          borderLeftColor: bedBounds.left ? BED_BORDER_COLOR : cellColorByKind.bed,
        } : undefined;

        // Planting dot: color by health, size by vegetation stage
        const dotColor = (() => {
          if (!showPlantingDot) return undefined;
          if (!healthInfo) return '#52c41a'; // default green
          switch (healthInfo.status) {
            case 'dead': return '#8c8c8c';
            case 'excellent': case 'good': return '#52c41a';
            case 'fair': return '#faad14';
            case 'poor': return '#d48806';
            case 'critical': return '#8B4513';
            default: return '#52c41a';
          }
        })();
        const dotBorderColor = (() => {
          if (!showPlantingDot) return undefined;
          if (!healthInfo) return '#389e0d';
          switch (healthInfo.status) {
            case 'dead': return '#595959';
            case 'excellent': case 'good': return '#389e0d';
            case 'fair': return '#d48806';
            case 'poor': return '#874d00';
            case 'critical': return '#5c2d0e';
            default: return '#389e0d';
          }
        })();
        const dotSize = (() => {
          if (!showPlantingDot) return 8;
          const stage = healthInfo?.vegetationStage;
          if (!stage) return 8;
          if (stage === 'dormancy' || stage === 'chilling' || stage === 'not_planted' || stage === 'germination' || stage === 'planting' || stage === 'rooting') return 5;
          if (stage === 'seedling' || stage === 'vegetative' || stage === 'awakening' || stage === 'bud_break' || stage === 'dieback' || stage === 'senescence') return 8;
          return 11; // flowering, fruiting, fruit_set, fruit_development, ripening, harvest_ready
        })();

        const cellCursor = (() => {
          if (props.appMode === 'planning' && props.allowBedSelection) return styles.cursorCrosshair;
          if (props.appMode === 'planting' && props.mode === 'erase') return styles.cursorCrosshair;
          if (props.appMode === 'planting' && cell?.kind === 'bed') return styles.cursorPointer;
          if (props.appMode === 'management' && cell?.kind === 'bed' && props.isTransplantTargetMode) {
            return styles.cursorImport;
          }
          if (props.appMode === 'management' && cell?.kind === 'bed') return styles.cursorPointer;
          if (props.appMode === 'planning' && !props.allowBedSelection && (cell?.kind === 'plot' || !cell)) return styles.cursorPointer;
          return styles.cursorDefault;
        })();

        const hoverBorders = isEntityHovered ? props.contourBorders.get(key) : null;
        const selectedBorders = isEntitySelected ? props.contourBorders.get(key) : null;

        const cellStyles = [
          styles.cell,
          cellCursor,
          isMultiSelected && styles.contourMultiSelected,
          isPlanningSelected && styles.planningSelected,
          selectedBorders && contourSelected(selectedBorders.top, selectedBorders.right, selectedBorders.bottom, selectedBorders.left),
          hoverBorders && contourHover(hoverBorders.top, hoverBorders.right, hoverBorders.bottom, hoverBorders.left),
          isHighlighted && styles.highlighted,
          isSelected && (selectionExceedsBoundary ? styles.selectionExceedsBoundary : (isOccupied ? styles.selectionOccupied : styles.selection)),
          isInBulkPreview && (isBulkPreviewValid ? styles.bulkPreviewValid : styles.bulkPreviewInvalid),
        ].filter(Boolean);

        return (
          <div
            key={key}
            css={cellStyles}
            data-plan-cell="true"
            data-row={props.rowGlobal}
            data-col={colGlobal}
            style={{ background, ...bedBorderStyle }}
            onMouseDown={(e) => props.onCellMouseDown(props.rowGlobal, colGlobal, e)}
            onMouseEnter={() => props.onCellMouseEnter(props.rowGlobal, colGlobal)}
            onMouseLeave={props.onCellMouseLeave}
            onMouseUp={props.onCellMouseUp}
          >  
            {showCovering && ghBorder && (
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  zIndex: 2,
                  borderStyle: 'solid',
                  borderColor: '#1a3a5c',
                  borderTopWidth: ghBorder.top ? 2 : 0,
                  borderRightWidth: ghBorder.right ? 2 : 0,
                  borderBottomWidth: ghBorder.bottom ? 2 : 0,
                  borderLeftWidth: ghBorder.left ? 2 : 0,
                }}
              />
            )}
            {showCovering && isGhTintActive && (
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(173, 216, 230, 0.30)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
            )}
            {showCovering && crossbarOrientation && (
              <span
                style={{
                  position: 'absolute',
                  ...(crossbarOrientation === 'vertical'
                    ? { top: 0, bottom: 0, left: '50%', width: 1, marginLeft: -0.5 }
                    : { left: 0, right: 0, top: '50%', height: 1, marginTop: -0.5 }),
                  backgroundColor: 'rgba(80, 80, 80, 0.25)',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
            )}
            {structureVisual && (
              <span
                title={structureVisual.title}
                style={{
                  position: 'absolute',
                  inset: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 3,
                  backgroundColor: structureVisual.color,
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  lineHeight: 1,
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                {structureVisual.label}
              </span>
            )}
            {showPlantingDot && (
              <span style={{
                ...styles.plantingIndicator,
                width: dotSize,
                height: dotSize,
                marginBottom: -(dotSize / 2),
                marginRight: -(dotSize / 2),
                backgroundColor: dotColor,
                border: `1px solid ${dotBorderColor}`,
              }} />
            )}
            {showWateringLayer && (
              <span style={{ ...styles.wateringIndicator, color: wateringColor }}>
                💧
              </span>
            )}
            {showIssuesLayer && (
              <span style={{ ...styles.issuesIndicator, backgroundColor: issuesColor }}>
                {issueInfo!.openIssues > 0 ? issueInfo!.openIssues : issueInfo!.recentIssues}
              </span>
            )}
            {showDiseaseLayer && (
              <span style={styles.diseaseIndicator} title={healthInfo!.activeDiseases!.map(d => d.code).join(', ')}>
                🦠
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PlanGridRow;
